import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import googleMapsService from '../services/googleMapsService';

const MapComponent = ({ userLocation, recommendations = [], onLocationSelect, onError, currentLanguage = 'zh-TW' }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    googleMapsService.initializeMap(containerRef.current, {
      center: userLocation || { lat: 25.033, lng: 121.5654 },
      styles: mapStyles
    }).then(({ map }) => {
      mapRef.current = map;
    }).catch(() => {
      const message = currentLanguage === 'zh-TW' ? '地圖暫時無法載入，仍可使用推薦清單' : 'The map is unavailable. The recommendation list still works.';
      setError(message);
      onError?.(message);
    });
  }, [currentLanguage, onError, userLocation]);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google) return;
    clearMarkers();
    const markers = [];
    const bounds = new window.google.maps.LatLngBounds();

    if (userLocation) {
      const marker = new window.google.maps.Marker({
        map: mapRef.current,
        position: userLocation,
        title: currentLanguage === 'zh-TW' ? '探索位置' : 'Search location',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#e85d1c',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3
        }
      });
      markers.push(marker);
      bounds.extend(userLocation);
    }

    recommendations.forEach((place, index) => {
      const location = place.geometry?.location;
      if (!location) return;
      const marker = new window.google.maps.Marker({
        map: mapRef.current,
        position: location,
        label: { text: String(index + 1), color: '#ffffff', fontWeight: '700' },
        title: place.name,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 15,
          fillColor: index < 3 ? '#30291f' : '#78716c',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3
        }
      });
      marker.addListener('click', () => onLocationSelect?.(place));
      markers.push(marker);
      bounds.extend(location);
    });

    markersRef.current = markers;
    if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds, 56);
    return clearMarkers;
  }, [clearMarkers, currentLanguage, onLocationSelect, recommendations, userLocation]);

  if (error) {
    return <div className="map-fallback" role="status"><MapPin aria-hidden="true" />{error}</div>;
  }
  return <div ref={containerRef} className="map-canvas" aria-label={currentLanguage === 'zh-TW' ? '推薦餐廳地圖' : 'Recommended places map'} />;
};

const mapStyles = [
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#dff1f1' }] },
  { featureType: 'landscape', elementType: 'geometry.fill', stylers: [{ color: '#f7f3ec' }] }
];

export default MapComponent;
